// components/AdminCoinToVipRequests.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

const AdminCoinToVipRequests = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [cancelHistory, setCancelHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentCancelPage, setCurrentCancelPage] = useState(1);
  const [totalCancelPages, setTotalCancelPages] = useState(1);
  const [activeTab, setActiveTab] = useState("requests");
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  // State cho tìm kiếm và lọc
  const [userSearch, setUserSearch] = useState("");
  const [planName, setPlanName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadRequests = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({ method: "get", url: `/api/admin/coin-to-vip-requests?page=${page}&limit=10` });
      setRequests(res.data.requests);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages);
      setLoading(false);
    } catch (error) {
      setError("Không thể tải danh sách yêu cầu nâng cấp gói VIP!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const loadCancelHistory = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", 10);
      if (userSearch) queryParams.append("userSearch", userSearch);
      if (planName) queryParams.append("planName", planName);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);

      const res = await fetchWithRetry({
        method: "get",
        url: `/api/admin/cancel-vip-history?${queryParams.toString()}`,
      });
      setCancelHistory(res.data.history);
      setCurrentCancelPage(res.data.currentPage);
      setTotalCancelPages(res.data.totalPages);
      setLoading(false);
    } catch (error) {
      setError("Không thể tải lịch sử hủy VIP!");
      setLoading(false);
    }
  }, [fetchWithRetry, userSearch, planName, startDate, endDate]);

  const handleRequestAction = async (requestId, action, reason = "") => {
    const confirmMessage =
      action === "approve"
        ? "Bạn có chắc muốn phê duyệt yêu cầu này?"
        : action === "reject"
        ? "Bạn có chắc muốn từ chối yêu cầu này?"
        : "Bạn có chắc muốn hủy gói VIP của người dùng này?";
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/coin-to-vip-requests",
        data: { requestId, action, reason },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadRequests(currentPage);
      setShowCancelForm(false);
      setCancelReason("");
      setSelectedRequestId(null);
      // Làm mới lịch sử hủy
      if (action === "cancelVip") {
        await loadCancelHistory(currentCancelPage);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Không thể xử lý yêu cầu!");
    }
  };

  const handleCancelVip = (requestId) => {
    setSelectedRequestId(requestId);
    setShowCancelForm(true);
  };

  const handleCancelSubmit = () => {
    if (!cancelReason.trim()) {
      setError("Vui lòng nhập lý do hủy!");
      return;
    }
    handleRequestAction(selectedRequestId, "cancelVip", cancelReason);
  };

  const handleSearch = () => {
    setCurrentCancelPage(1); // Reset về trang 1 khi tìm kiếm
    loadCancelHistory(1);
  };

  const handleReset = () => {
    setUserSearch("");
    setPlanName("");
    setStartDate("");
    setEndDate("");
    setCurrentCancelPage(1);
    loadCancelHistory(1);
  };

  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    if (activeTab === "requests") {
      loadRequests();
    } else if (activeTab === "cancelHistory") {
      loadCancelHistory();
    }

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "membership_upgrade_request") {
        setSuccessMessage(`Yêu cầu nâng cấp gói VIP mới đã được gửi: ${notification.message}`);
        loadRequests(currentPage);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadRequests, loadCancelHistory, isLoading, currentPage, currentCancelPage, activeTab]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Quản lý yêu cầu nâng cấp gói VIP bằng Coin</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{successMessage}</div>
      )}

      <div className="mb-8">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setActiveTab("requests")}
            className={`py-2 px-4 font-semibold ${activeTab === "requests" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"}`}
          >
            Yêu cầu nâng cấp
          </button>
          <button
            onClick={() => setActiveTab("cancelHistory")}
            className={`py-2 px-4 font-semibold ${activeTab === "cancelHistory" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"}`}
          >
            Lịch sử hủy VIP
          </button>
        </div>
      </div>

      {activeTab === "requests" && (
        <>
          {requests.length === 0 && !error && !loading && (
            <p className="text-center text-gray-500">Không có yêu cầu nâng cấp gói VIP nào được tìm thấy.</p>
          )}

          {requests.length > 0 && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="overflow-x-auto"
              >
                <table className="w-full bg-white rounded-lg shadow-lg">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="p-4 text-left">Người dùng</th>
                      <th className="p-4 text-left">Gói VIP</th>
                      <th className="p-4 text-left">Số coin</th>
                      <th className="p-4 text-left">Trạng thái</th>
                      <th className="p-4 text-left">Ngày yêu cầu</th>
                      <th className="p-4 text-left">Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request._id} className="border-b hover:bg-gray-50 transition duration-200">
                        <td className="p-4">
                          {request.userId ? `${request.userId.name} (${request.userId.email})` : "Người dùng không xác định"}
                        </td>
                        <td className="p-4">{request.planId ? request.planId.name : "Gói không xác định"}</td>
                        <td className="p-4">{request.coinAmount.toLocaleString()}</td>
                        <td className="p-4">
                          {request.status === "pending"
                            ? "Chờ xử lý"
                            : request.status === "approved"
                            ? "Đã phê duyệt"
                            : request.status === "rejected"
                            ? "Đã từ chối"
                            : "Đã hủy"}
                        </td>
                        <td className="p-4">{new Date(request.createdAt).toLocaleString("vi-VN")}</td>
                        <td className="p-4 space-x-2">
                          {request.status === "pending" && (
                            <>
                              <button
                                onClick={() => handleRequestAction(request._id, "approve")}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                              >
                                Phê duyệt
                              </button>
                              <button
                                onClick={() => handleRequestAction(request._id, "reject")}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                              >
                                Từ chối
                              </button>
                            </>
                          )}
                          {request.status === "approved" && (
                            <button
                              onClick={() => handleCancelVip(request._id)}
                              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                            >
                              Hủy VIP
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>

              {/* Phân trang cho yêu cầu nâng cấp */}
              <div className="flex justify-center mt-4">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => loadRequests(page)}
                    className={`mx-1 px-4 py-2 rounded-lg ${currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === "cancelHistory" && (
        <>
          {/* Form tìm kiếm và lọc */}
          <div className="mb-6 p-4 bg-white rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Tìm kiếm và lọc lịch sử hủy VIP</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Tìm kiếm người dùng</label>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Nhập tên hoặc email người dùng"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Tên gói VIP</label>
                <input
                  type="text"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Nhập tên gói VIP"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Từ ngày</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Đến ngày</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Đặt lại
              </button>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Tìm kiếm
              </button>
            </div>
          </div>

          {cancelHistory.length === 0 && !error && !loading && (
            <p className="text-center text-gray-500">Không có lịch sử hủy VIP nào được tìm thấy.</p>
          )}

          {cancelHistory.length > 0 && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="overflow-x-auto"
              >
                <table className="w-full bg-white rounded-lg shadow-lg">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="p-4 text-left">Người dùng</th>
                      <th className="p-4 text-left">Gói VIP</th>
                      <th className="p-4 text-left">Quản trị viên</th>
                      <th className="p-4 text-left">Lý do hủy</th>
                      <th className="p-4 text-left">Ngày hủy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cancelHistory.map((record) => (
                      <tr key={record._id} className="border-b hover:bg-gray-50 transition duration-200">
                        <td className="p-4">
                          {record.userId ? `${record.userId.name} (${record.userId.email})` : "Người dùng không xác định"}
                        </td>
                        <td className="p-4">{record.planId ? record.planId.name : "Gói không xác định"}</td>
                        <td className="p-4">{record.adminId ? record.adminId.name : "Quản trị viên không xác định"}</td>
                        <td className="p-4">{record.reason}</td>
                        <td className="p-4">{new Date(record.canceledAt).toLocaleString("vi-VN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>

              {/* Phân trang cho lịch sử hủy */}
              <div className="flex justify-center mt-4">
                {Array.from({ length: totalCancelPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => loadCancelHistory(page)}
                    className={`mx-1 px-4 py-2 rounded-lg ${currentCancelPage === page ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Form nhập lý do hủy */}
      {showCancelForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Nhập lý do hủy VIP</h2>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Lý do hủy</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full p-2 border rounded-lg"
                placeholder="Nhập lý do hủy VIP"
                rows="4"
                required
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowCancelForm(false);
                  setCancelReason("");
                  setSelectedRequestId(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handleCancelSubmit}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoinToVipRequests;